/**
 * Jeweller's loupe on product photos: a round 2.2× lens follows the pointer over the main gallery
 * images so the iris and the bezel can be studied up close. Fine pointers only; reduced motion and
 * touch keep Horizon's own zoom dialog, which a click still opens (the lens ignores pointer events).
 * Styles live in snippets/macro-loupe.liquid.
 */

const ZOOM = 2.2;
const LENS_SIZE = 190;
const SELECTOR = 'media-gallery .product-media-container--image .product-media';

const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * The drawn image area inside an `object-fit: contain` img.
 * @param {HTMLImageElement} img
 */
function contentRect(img) {
  const box = img.getBoundingClientRect();
  const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : box.width / box.height;
  let width = box.width;
  let height = width / ratio;
  if (height > box.height) {
    height = box.height;
    width = height * ratio;
  }
  return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
}

/** @param {HTMLElement} media */
function attach(media) {
  if (media.dataset.loupe) return;
  const img = media.querySelector('img.product-media__image, img');
  if (!(img instanceof HTMLImageElement)) return;
  media.dataset.loupe = 'true';

  const lens = document.createElement('span');
  lens.className = 'macro-loupe';
  lens.setAttribute('aria-hidden', 'true');
  media.append(lens);

  /** @param {PointerEvent} event */
  const move = (event) => {
    if (event.pointerType !== 'mouse' || reduceMotion.matches || !canHover.matches) return;
    const area = contentRect(img);
    const x = event.clientX - area.left;
    const y = event.clientY - area.top;
    const inside = x >= 0 && y >= 0 && x <= area.width && y <= area.height;
    lens.toggleAttribute('data-active', inside);
    if (!inside) return;

    const host = media.getBoundingClientRect();
    lens.style.backgroundImage = `url("${img.currentSrc || img.src}")`;
    lens.style.backgroundSize = `${area.width * ZOOM}px ${area.height * ZOOM}px`;
    lens.style.backgroundPosition = `${LENS_SIZE / 2 - x * ZOOM}px ${LENS_SIZE / 2 - y * ZOOM}px`;
    lens.style.transform = `translate(${event.clientX - host.left - LENS_SIZE / 2}px, ${event.clientY - host.top - LENS_SIZE / 2}px)`;
  };
  const leave = () => lens.removeAttribute('data-active');

  // Horizon layers its zoom button over the photo, so listen on the whole slide.
  const surface = media.closest('.product-media-container') ?? media;
  surface.addEventListener('pointermove', move);
  surface.addEventListener('pointerleave', leave);
}

function init() {
  for (const media of document.querySelectorAll(SELECTOR)) {
    if (media instanceof HTMLElement) attach(media);
  }
}

init();

// Variant changes and section re-renders replace the gallery markup; re-scan at most once a frame.
let scheduled = 0;
new MutationObserver(() => {
  if (scheduled) return;
  scheduled = requestAnimationFrame(() => {
    scheduled = 0;
    init();
  });
}).observe(document.body, { childList: true, subtree: true });
